// @flow
import type { Dispatch } from 'redux';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { getURLWithQueryString } from '@finect/front-utils/getURLWithQueryString';
import { setError } from '../error/actions';
import type { QuestionType } from '../../types';
import { normalize2, models } from '../../utils';

export function setQuestions(questions: Object) {
  return {
    type: 'SET_QUESTIONS',
    questions
  };
}

export function setMoreQuestions(questions: Object) {
  return {
    type: 'MORE_QUESTIONS',
    questions
  };
}

export function setPreFetchedQuestions(questions: Object) {
  return {
    type: 'PREFETCHED_QUESTIONS',
    questions
  };
}

export function setQuestion(question: QuestionType) {
  return {
    type: 'SET_QUESTION',
    question
  };
}

export function setQuestionProducts(questionId: number, products: Array<Object>) {
  return {
    type: 'SET_QUESTION_PRODUCTS',
    questionId,
    products
  };
}

export function removeQuestion(questionId: number) {
  return {
    type: 'REMOVE_QUESTION',
    questionId
  };
}

export function voteQuestion(questionId: number, vote: boolean) {
  return {
    type: 'OPTIMISTIC_VOTE',
    questionId,
    vote
  };
}

export function voteAnswer(answerId: number, vote: boolean, questionId: string) {
  return {
    type: 'OPTIMISTIC_VOTE_ANSWER',
    answerId,
    vote,
    questionId
  };
}

export function optimisticFollow(questionId: number, follow: boolean) {
  return {
    type: 'OPTIMISTIC_FOLLOW',
    questionId,
    follow
  };
}

export function getQuestions(questionType?: string, tag?: number | null, offset?: number | null, cookie?: string) {
  let type = '';
  const params = {};

  // TODO: Debe venir un data.authors también, si no authors va vacío y falla en QuestionPreview
  // if (questionType === 'followedQuestions') {
  //   type = 'question';
  //   params.type = type;
  //   return (dispatch: Dispatch) =>
  //     finectAPI({
  //       version: 'v4',
  //       path: getURLWithQueryString('users/me/subscriptions', params),
  //       method: 'GET',
  //       credentials: cookie,
  //       onSuccess: ({ data }) => {
  //         const questions = data.map(item => item.entity);
  //         const modeledQuestions = questions.map(item => models.questionV4(item, data.authors));
  //         const normQuestions = normalize2(modeledQuestions);
  //         questions.length === 10 ? (normQuestions.more = true) : (normQuestions.more = false);
  //         normQuestions.type = questionType || 'latest';
  //         normQuestions.isTag = Number(tag || 0);
  //         offset ? dispatch(setMoreQuestions(normQuestions)) : dispatch(setQuestions(normQuestions));
  //       },
  //       onError: error => dispatch(setError(error))
  //     });
  // }

  // if (questionType === 'mine') {
  //   type = 'question';
  //   params.type = type;
  //   return (dispatch: Dispatch) =>
  //     finectAPI({
  //       version: 'v4',
  //       path: getURLWithQueryString('users/me/questions'),
  //       method: 'GET',
  //       credentials: cookie,
  //       onSuccess: ({ data }) => {
  //         const questions = data.questions.map(item => models.questionV4(item, data.authors))
  //           .slice()
  //           .reverse();
  //         const normQuestions = normalize2(questions);
  //         questions.length === 10 ? (normQuestions.more = true) : (normQuestions.more = false);
  //         normQuestions.type = questionType || 'latest';
  //         normQuestions.isTag = Number(tag || 0);
  //         offset ? dispatch(setMoreQuestions(normQuestions)) : dispatch(setQuestions(normQuestions));
  //       },
  //       onError: error => dispatch(setError(error))
  //     });
  // }

  if (questionType === 'active') {
    type = '-revision/activity';
  }
  if (questionType === 'latest') {
    type = '-revision/created';
  }

  if (type) {
    params.orderby = type;
  }
  if (tag) {
    params.filter = `tag eq ${tag}`;
  }
  if (offset) {
    params.offset = offset;
  }

  const path = getURLWithQueryString('contents/questions', params);
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path,
      method: 'GET',
      credentials: cookie,
      onSuccess: ({ data }) => {
        const questions = data.questions.map(item => models.questionV4(item, data.authors));
        const normQuestions = normalize2(questions);
        questions.length === 10 ? (normQuestions.more = true) : (normQuestions.more = false);
        normQuestions.type = questionType || 'latest';
        normQuestions.isTag = Number(tag || 0);
        offset ? dispatch(setMoreQuestions(normQuestions)) : dispatch(setQuestions(normQuestions));
      },
      onError: error => dispatch(setError(error))
    });
}

export function preFetchedQuestions(questions: Array<QuestionType>, ignoreId?: number | string) {
  return (dispatch: Dispatch) => {
    const normQuestions = normalize2(questions);
    ignoreId && delete normQuestions.items[Number(ignoreId)];
    dispatch(setPreFetchedQuestions(normQuestions));
  };
}

export function getQuestion(questionId: number, cookie?: string) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path: `contents/questions/${questionId}`,
      method: 'GET',
      credentials: cookie,
      onSuccess: ({ data }: Object) => {
        const modelizedQuestion = models.questionV4(data.question, data.authors);
        dispatch(setQuestion(modelizedQuestion));
      },
      onError: (error) => {
        if (error.code !== 404) dispatch(setError(error));
      }
    });
}

export function getQuestionProducts(questionId: number, cookie?: string) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path: `contents/questions/${questionId}/products`,
      method: 'GET',
      credentials: cookie,
      onSuccess: ({ data }: Object) => {
        dispatch(setQuestionProducts(questionId, data));
      },
      onError: (error) => {
        if (error.code !== 404) dispatch(setError(error));
      }
    });
}

/*
TODO: moderación de preguntas y respuestas
· Cuando creas una pregunta mandar a una ThankYou page
· Cuando creas respuesta, coger isModerated
    Solo la ve el autor con un mensaje de "Pendiente de moderación"
*/

export function sendQuestion(question: Object, cb: Function) {
  const newQuestion = {
    title: question.title,
    body: JSON.stringify(question.rawContent),
    plain_text: question.plainText, // eslint-disable-line
    tags: question.tags,
    products: question.products,
    group: question.group
  };

  return (dispatch: Dispatch) => {
    finectAPI({
      version: 'v3',
      path: `questions`,
      method: 'POST',
      body: newQuestion,
      onSuccess: (createdQuestion: Object) => {
        const modelizedQuestion = models.questions(createdQuestion);
        dispatch(setQuestion(modelizedQuestion));
        return cb(createdQuestion);
      },
      onError: error => dispatch(setError(error))
    });
  };
}

export function editQuestion(questionId: number, question: Object) {
  const newQuestion = {
    title: question.title,
    body: JSON.stringify(question.rawContent),
    plain_text: question.plainText, // eslint-disable-line
    tags: question.tags,
    products: question.products
  };

  return (dispatch: Dispatch) => {
    finectAPI({
      version: 'v3',
      path: `questions/${questionId}`,
      method: 'PUT',
      body: newQuestion,
      onSuccess: (editedQuestion: Object) => {
        // const modelizedQuestion = models.questions(editedQuestion);
        // dispatch(setQuestion(modelizedQuestion));
        dispatch(getQuestion(questionId));
      },
      onError: error => dispatch(setError(error))
    });
  };
}

export function deleteQuestion(questionId: number, cb: Function) {
  return (dispatch: Dispatch) => {
    finectAPI({
      version: 'v3',
      path: `questions/${questionId}`,
      method: 'DELETE',
      onSuccess: (response: Object) => {
        dispatch(removeQuestion(response.id));
        return cb();
      },
      onError: error => dispatch(setError(error))
    });
  };
}
